import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select, Textarea } from "@/components/base/FormControls";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import type { PlatformLocataire, PlatformLocataireInput } from "@/store/platform/types";
import { TYPE_PIECE_OPTIONS, type TypePieceIdentite } from "@/pages/proprietaire/locataires/types";

interface PlatformLocataireFormProps {
  open: boolean;
  onClose: () => void;
  locataire: PlatformLocataire | null;
}

interface FormState {
  prenom: string;
  nom: string;
  telephone: string;
  email: string;
  adresse: string;
  profession: string;
  typePiece: TypePieceIdentite | "";
  numeroPiece: string;
  contactUrgence: string;
  notes: string;
}

const EMPTY_STATE: FormState = {
  prenom: "",
  nom: "",
  telephone: "",
  email: "",
  adresse: "",
  profession: "",
  typePiece: "",
  numeroPiece: "",
  contactUrgence: "",
  notes: "",
};

/**
 * Édition d'un locataire depuis le Super Admin. Le locataire reste une entité
 * indépendante : la relation avec un bien se fait par le contrat.
 */
export default function PlatformLocataireForm({ open, onClose, locataire }: PlatformLocataireFormProps) {
  const { updateLocataire } = usePlatformData();
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open || !locataire) return;
    setForm({
      prenom: locataire.prenom,
      nom: locataire.nom,
      telephone: locataire.telephone,
      email: locataire.email,
      adresse: locataire.adresse,
      profession: locataire.profession,
      typePiece: locataire.typePiece as TypePieceIdentite | "",
      numeroPiece: locataire.numeroPiece,
      contactUrgence: locataire.contactUrgence,
      notes: locataire.notes,
    });
    setErrors({});
  }, [open, locataire]);

  if (!locataire) return null;

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    const nextErrors: Record<string, string> = {};
    if (!form.prenom.trim()) nextErrors.prenom = "Le prénom est obligatoire.";
    if (!form.nom.trim()) nextErrors.nom = "Le nom est obligatoire.";
    if (!form.telephone.trim()) nextErrors.telephone = "Le téléphone est obligatoire.";
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      nextErrors.email = "Indiquez une adresse email valide.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const input: PlatformLocataireInput = {
      scope: locataire.scope,
      prenom: form.prenom.trim(),
      nom: form.nom.trim(),
      telephone: form.telephone.trim(),
      email: form.email.trim(),
      adresse: form.adresse.trim(),
      profession: form.profession.trim(),
      typePiece: form.typePiece,
      numeroPiece: form.numeroPiece.trim(),
      contactUrgence: form.contactUrgence.trim(),
      notes: form.notes.trim(),
    };
    updateLocataire(locataire.id, input);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Modifier le locataire"
      description="Corrigez la fiche de ce locataire. Le locataire reste indépendant : la relation avec un bien se fait par le contrat."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-save-line text-base" aria-hidden="true" />
            Enregistrer
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
        <Field label="Prénom" htmlFor="sa-loc-prenom" required error={errors.prenom}>
          <Input
            id="sa-loc-prenom"
            value={form.prenom}
            onChange={(event) => update("prenom", event.target.value)}
            invalid={Boolean(errors.prenom)}
            maxLength={60}
          />
        </Field>
        <Field label="Nom" htmlFor="sa-loc-nom" required error={errors.nom}>
          <Input
            id="sa-loc-nom"
            value={form.nom}
            onChange={(event) => update("nom", event.target.value)}
            invalid={Boolean(errors.nom)}
            maxLength={60}
          />
        </Field>
        <Field label="Téléphone" htmlFor="sa-loc-telephone" required error={errors.telephone}>
          <Input
            id="sa-loc-telephone"
            type="tel"
            value={form.telephone}
            onChange={(event) => update("telephone", event.target.value)}
            invalid={Boolean(errors.telephone)}
            maxLength={40}
          />
        </Field>
        <Field label="Email" htmlFor="sa-loc-email" error={errors.email}>
          <Input
            id="sa-loc-email"
            type="email"
            value={form.email}
            onChange={(event) => update("email", event.target.value)}
            invalid={Boolean(errors.email)}
            maxLength={120}
          />
        </Field>
        <Field label="Profession" htmlFor="sa-loc-profession">
          <Input
            id="sa-loc-profession"
            value={form.profession}
            onChange={(event) => update("profession", event.target.value)}
            maxLength={80}
          />
        </Field>
        <Field label="Contact d'urgence" htmlFor="sa-loc-urgence">
          <Input
            id="sa-loc-urgence"
            value={form.contactUrgence}
            onChange={(event) => update("contactUrgence", event.target.value)}
            maxLength={120}
          />
        </Field>
        <Field label="Adresse" htmlFor="sa-loc-adresse" className="sm:col-span-2">
          <Input
            id="sa-loc-adresse"
            value={form.adresse}
            onChange={(event) => update("adresse", event.target.value)}
            maxLength={160}
          />
        </Field>
        <Field label="Type de pièce d'identité" htmlFor="sa-loc-type-piece">
          <Select
            id="sa-loc-type-piece"
            value={form.typePiece}
            onChange={(event) => update("typePiece", event.target.value)}
            options={TYPE_PIECE_OPTIONS}
            placeholder="Sélectionnez un type"
          />
        </Field>
        <Field label="Numéro de pièce" htmlFor="sa-loc-numero-piece">
          <Input
            id="sa-loc-numero-piece"
            value={form.numeroPiece}
            onChange={(event) => update("numeroPiece", event.target.value)}
            maxLength={60}
          />
        </Field>
        <Field label="Notes" htmlFor="sa-loc-notes" className="sm:col-span-2">
          <Textarea
            id="sa-loc-notes"
            value={form.notes}
            onChange={(event) => update("notes", event.target.value)}
            className="min-h-[88px]"
            maxLength={600}
          />
        </Field>
      </div>
    </Modal>
  );
}